import { Injectable } from '@angular/core';
import { Observable, from, of, switchMap, map, catchError } from 'rxjs';
import { AuthService } from '../auth/services/auth.service';
import config from '../../../config.js';
import { HttpClient, HttpParams } from '@angular/common/http';

// Actions (optional future interactive buttons)
export type NotificationAction = {
  label: string;
  kind?: 'primary' | 'secondary';
  icon?: string;
  onClick?: () => void;
};

// Raw backend DTO with optional UI augmentation fields.
export interface NotificationDto {
  id: number;
  notification_type_id: number;
  notification_type: string; // e.g. LEGAL_ENTITY_CHANGE_STATUS_REQUEST
  notification_type_description?: string; // e.g. Status change request
  notification_status_id: number;
  notification_status: string; // e.g. NEW | READ
  from_user_prid: string;
  to_user_prid: string;
  previous_notification_id: number | null;
  message: string;
  json_payload: unknown | null;
  link: string | null;
  created_date: number; // epoch seconds
  last_updated_date: number | null; // epoch seconds
  read_at: number | null; // epoch seconds
  // UI augmentation (not from backend):
  time?: string; // derived from last_updated_date (fallback created_date)
  actions?: NotificationAction[]; // optional interactive actions
}

@Injectable({ providedIn: 'root' })
export class NotificationService {
  // Align base URL construction with other services (e.g. LegalEntityService / TodoService)
  // Remove any trailing slash to avoid double slashes when concatenating.
  private readonly base = (config.API_GATEWAY_ENDPOINT || '').replace(/\/$/, '');
  private readonly notificationsBase = `${this.base}/notifications/user`;

  constructor(
    private auth: AuthService,
    private http: HttpClient,
  ) {}

  private getUserPrid(): Observable<string> {
    interface AmplifySessionLike {
      tokens?: { idToken?: { payload?: { preferred_username?: string } } };
    }
    return from(this.auth.amplifyGetUserSession().toPromise()).pipe(
      map((session: unknown) => {
        const s = session as AmplifySessionLike | undefined;
        const preferred: string | undefined = s?.tokens?.idToken?.payload?.preferred_username;
        return preferred ? preferred.split('@')[0] : '';
      }),
      catchError(() => of('')),
    );
  }

  getNotifications(
    _status: 'NEW' | 'READ' = 'NEW', // underscore: currently unused but preserved for future filter activation
    pageSize = 20,
    page = 1,
  ): Observable<NotificationDto[]> {
    return this.getUserPrid().pipe(
      switchMap((prid) => {
        if (!prid) return of([]);
        const endpoint = `${this.notificationsBase}/${encodeURIComponent(prid)}`;
        const params = new HttpParams()
          .set('page_size', String(pageSize))
          /* .set('notification_status', _status) */
          .set('page', String(page));
        // Use HttpClient so TokenInterceptor attaches auth headers.
        return this.http.get<NotificationDto[]>(endpoint, { params }).pipe(
          map((raw: NotificationDto[] | null) => (raw || []).map((r) => this.augmentDto(r))),
          catchError(() => of([])),
        );
      }),
    );
  }
  private augmentDto(raw: NotificationDto): NotificationDto {
    const baseEpoch = raw.created_date;
    const time = this.formatRelativeTime(baseEpoch);
    return { ...raw, time };
  }

  /**
   * Marks a notification as READ.
   * @param notificationId - The ID of the notification to mark as read
   * @returns Observable with the API response
   */
  markAsRead(notificationId: number): Observable<{ http_status_code: number; message: string }> {
    const endpoint = `${this.base}/notifications/mark_as_read/${notificationId}`;
    return this.http.put<{ http_status_code: number; message: string }>(endpoint, {}).pipe(
      catchError(() => {
        return of({ http_status_code: 500, message: 'Failed to mark notification as read' });
      }),
    );
  }

  /**
   * Formats epoch seconds into a relative string following rules:
   * - < 60 minutes: "X min ago" | "X mins ago"
   * - < 24 hours: "X hr ago" | "X hrs ago"
   * - >= 24 hours: dd/Mon/yyyy (UTC date derived from timestamp)
   * If epoch invalid/undefined returns empty string.
   */
  private formatRelativeTime(epochSeconds?: number | null): string {
    if (!epochSeconds || epochSeconds <= 0) return '';
    const nowMs = Date.now();
    const tsMs = epochSeconds * 1000;
    const diffMs = Math.max(0, nowMs - tsMs);
    const diffMinutes = Math.floor(diffMs / 60000);
    if (diffMinutes < 60) {
      const v = diffMinutes;
      return v === 1 ? '1 min ago' : `${v} mins ago`;
    }
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) {
      const v = diffHours;
      return v === 1 ? '1 hr ago' : `${v} hrs ago`;
    }
    // Date format: dd/Mon/yyyy using UTC components and English month abbreviations
    const date = new Date(tsMs);
    const day = String(date.getUTCDate()).padStart(2, '0');
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const month = monthNames[date.getUTCMonth()];
    const year = date.getUTCFullYear();
    return `${day}/${month}/${year}`;
  }
}
